import { supabase } from '../supabase'
import { isAuthDebugEnabled } from '../debug'
import type { UserPreferences, UserPreferencesUpdate } from '../../types/userPreferences'

/**
 * Fetch user preferences for the current user
 */
export async function getUserPreferences(): Promise<UserPreferences | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    // During startup the auth state may not be established yet.
    // Return null so callers can wait for the next auth change instead of throwing.
    if (import.meta.env.DEV) {
      console.warn('[userPreferences] getUserPreferences called without auth user')
    }
    return null
  }

  return getUserPreferencesForUser(user.id)
}

export async function getUserPreferencesForUser(userId: string): Promise<UserPreferences | null> {
  const { data, error } = await supabase
    .from('user_preferences')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()

  if (error) {
    // AbortError is expected during component unmount/auth changes - don't log as error
    if (error.message?.includes('AbortError') || error.code === 'PGRST301') {
      if (import.meta.env.DEV) {
        console.debug('[userPreferences] Query aborted (component unmount or auth change)')
      }
      return null
    }

    console.error('[userPreferences] Error fetching preferences:', error)
    // Don't throw - return null and let the app work with defaults
    return null
  }

  // If preferences don't exist (shouldn't happen, but handle gracefully)
  if (!data) {
    return await handleMissingPreferences(userId)
  }

  return data
}

/**
 * Handle missing preferences
 * Note: Preferences should be auto-created by database trigger on account creation
 * If missing, return null so the app uses default values
 */
async function handleMissingPreferences(userId: string): Promise<UserPreferences | null> {
  if (import.meta.env.DEV) {
    console.log('[userPreferences] Preferences not found for user:', userId)
    console.log(
      '[userPreferences] Using default values. Preferences should exist for all accounts.',
    )
  }

  // Return null - AccessibilityContext will use default values
  return null
}

/**
 * Update user preferences
 */
export async function updateUserPreferences(
  updates: UserPreferencesUpdate,
): Promise<UserPreferences> {
  console.log('[userPreferences] updateUserPreferences called with:', updates)

  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    console.error('[userPreferences] No authenticated user')
    throw new Error('No authenticated user')
  }

  console.log('[userPreferences] Updating for user:', user.id)

  // Get the account_id for RLS policy check
  const { data: account } = await supabase
    .from('accounts')
    .select('id')
    .eq('user_id', user.id)
    .single()

  if (!account) {
    console.error('[userPreferences] No account found for user')
    throw new Error('Account not found')
  }

  // Upsert with both user_id and account_id for RLS
  const { data, error } = await supabase
    .from('user_preferences')
    .upsert(
      {
        ...updates,
        user_id: user.id,
        account_id: account.id,
      },
      {
        onConflict: 'user_id',
      },
    )
    .select()
    .single()

  if (error) {
    console.error('[userPreferences] Update error:', error)
    throw error
  }

  console.log('[userPreferences] Update successful, returned data:', data)
  return data
}

/**
 * Subscribe to preference changes
 * NOTE: Temporarily disabled due to WebSocket connection issues with localhost
 * The app will still work - preferences just won't update in real-time
 */
export function subscribeToPreferences(
  userId: string,
  callback: (preferences: UserPreferences) => void,
) {
  // Temporarily disable realtime subscriptions to prevent localhost connection errors
  // TODO: Re-enable once Supabase realtime URL issue is resolved
  if (isAuthDebugEnabled()) {
    console.log('[userPreferences] Realtime subscription disabled (temporary)')
  }

  // Return a no-op unsubscribe function
  return () => {
    // No-op: nothing to unsubscribe from
  }

  /* DISABLED - Re-enable once realtime URL issue is fixed
  const channel = supabase
    .channel(`user_preferences:${userId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'user_preferences',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        callback(payload.new as UserPreferences);
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        if (import.meta.env.DEV) {
          console.log('[userPreferences] Realtime subscription active');
        }
      } else if (status === 'CHANNEL_ERROR') {
        // Silently handle realtime connection errors - they're not critical
        // The app will still work with polling/refresh
        if (import.meta.env.DEV) {
          console.warn('[userPreferences] Realtime subscription error (non-critical)');
        }
      }
    });

  return () => {
    supabase.removeChannel(channel);
  };
  */
}
